import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { avatarColors, avatarInitials } from "../utils/avatar.util";

/**
 * A person as a small round picture: their photo when they have one, otherwise the first letters of their first
 * and last name (John Factor gives JF) on a colour that is the same every time for the same person.
 */
@Component({
  selector: "app-user-avatar",
  standalone: true,
  imports: [CommonModule],
  template: `
    <span
      class="ua"
      [style.width.px]="size"
      [style.height.px]="size"
      [style.minWidth.px]="size"
      [style.fontSize.px]="size * 0.4"
      [style.background]="showImage ? '#fff' : colors.bg"
      [style.color]="colors.fg"
      [attr.title]="name || null"
      data-testid="user-avatar"
    >
      <img *ngIf="showImage" [src]="url" alt="" class="ua__img" (error)="imageFailed = true" />
      <ng-container *ngIf="!showImage">{{ initials }}</ng-container>
    </span>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        flex: none;
      }
      .ua {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        aspect-ratio: 1 / 1;
        overflow: hidden;
        border-radius: 9999px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.02em;
        user-select: none;
      }
      .ua__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    `,
  ],
})
export class UserAvatarComponent {
  /** The person full name: gives the letters and the colour. */
  @Input() name: string | null | undefined = "";
  /** Their photo; a missing or broken one falls back to the letters. */
  @Input() set url(value: string | null | undefined) {
    this._url = value || null;
    this.imageFailed = false;
  }
  get url(): string | null {
    return this._url;
  }
  @Input() size = 30;

  imageFailed = false;
  private _url: string | null = null;

  get showImage(): boolean {
    return !!this._url && !this.imageFailed;
  }

  get initials(): string {
    return avatarInitials(this.name);
  }

  get colors(): { bg: string; fg: string } {
    return avatarColors(this.name);
  }
}
